import {
  PAYMENT_ERROR_STATUS,
  PAYMENT_OVERDUE_STATUS,
  PAYMENT_PAID_STATUS,
  PAYMENT_REFUSED_STATUS,
  PAYMENT_WAITING_PAYMENT_STATUS,
} from '@constants/payment'
import {
  IBolixInstallmentOrder,
  Order,
  IPayment,
  ISmartInstallmentOrder,
  ISubscriptionOrder,
  OfferKind,
  PaymentStatusType,
} from '@dtos/Order'
import { getOrder, getPublicOrder } from '@services/api/order'
import { formatDateShort } from '@utils/dateFormatter'
import { formatMoney } from '@utils/moneyFormatter'
import first from 'lodash/first'
import { useQuery } from 'react-query'

const STRATEGY_SUBSCRIPTION_TYPES = ['smart_installment', 'installment']

// Tentativa que não gerou cobrança: nem vira o pagamento "relevante" da tela,
// nem entra na soma do total. Constantes tipadas e não strings soltas, senão um
// erro de digitação compila e a tentativa volta a ser contada em silêncio.
export const INVALID_PAYMENT_STATUSES: PaymentStatusType[] = [
  PAYMENT_REFUSED_STATUS,
  PAYMENT_ERROR_STATUS,
]

// Único lugar que decide se uma linha de pagamento conta. Serve tanto o
// `IPayment` cru quanto o já formatado, para o total e a lista da tela não
// discordarem sobre o que é tentativa fracassada.
const isChargeable = (p: { status: PaymentStatusType }) =>
  !INVALID_PAYMENT_STATUSES.includes(p.status)

// Opções de moeda da compra internacional. Um lugar só, para a linha e o total
// não formatarem o mesmo dinheiro de jeitos diferentes.
const moneyOptionsFor = (quotation: IPayment['international_quotation']) => ({
  currency: quotation?.code,
  hasDecimals: quotation?.has_decimal ?? true,
})

function checkIsSubscription(order: Order): order is ISubscriptionOrder {
  return order.subscription.subscription_type === 'recurrency'
}

function checkIsSmartInstallment(
  order: Order,
): order is ISmartInstallmentOrder {
  return order.subscription?.subscription_type === 'smart_installment'
}

function checkIsStrategySales(
  order: Order,
): order is ISmartInstallmentOrder | IBolixInstallmentOrder {
  return (
    order.kind === 'common' &&
    STRATEGY_SUBSCRIPTION_TYPES.includes(order.subscription?.subscription_type)
  )
}

// Exportado para o spec: a trava abaixo é a única coisa que separa "copiar o
// código" de "copiar um código morto", e testar por fora custaria react-query e
// mock de API para as mesmas asserções.
export function formatPayments(
  payments: IPayment[],
  isSmartInstallment: boolean,
  orderKind: Order['kind'],
) {
  // HET-3806: o código só é oferecido enquanto a cobrança está aberta. O banco
  // continua guardando `pix_qr_code` e `bank_slip_file_url` depois de paga,
  // cancelada ou estornada, e sem esta trava a tela manda o comprador copiar
  // código morto. O boleto já respeitava a regra; o pix não.
  const abertaParaPagamento = [
    PAYMENT_WAITING_PAYMENT_STATUS,
    PAYMENT_OVERDUE_STATUS,
  ]

  const isCommonOrder = orderKind === 'common' && !isSmartInstallment
  const hasNonRefusedPayments = payments.some((p) => p.status !== 'refused')

  const filteredPayments =
    isCommonOrder && hasNonRefusedPayments
      ? payments.filter((p) => p.status !== 'refused')
      : payments

  const formattedPayments = filteredPayments
    .sort((a, b) => Date.parse(a.due_at) - Date.parse(b.due_at))
    .map((p) => {
      const isOpen = abertaParaPagamento.includes(p.status)
      const isInternational = isInternationalPayment(p)
      const amount = isInternational
        ? formatMoney(
            p.currency_price_cents || 0,
            moneyOptionsFor(p.international_quotation),
          )
        : formatMoney(p.total_amount_to_be_paid_cents)

      return {
        amount,
        totalAmountCents: p.total_amount_to_be_paid_cents,
        currencyPriceCents: p.currency_price_cents || 0,
        internationalQuotation: p.international_quotation,
        paymentDate: formatDateShort(p.due_at),
        status: p.status,
        bankSlipURL: isOpen ? p.bank_slip_file_url : null,
        paymentMethod: p.payment_method,
        gatewayName: p.gateway_name ?? null,
        dueAt: new Date(p.due_at),
        pixQrCode: isOpen ? p.pix_qr_code : null,
        id: p.id,
        buyer: p.buyer,
        isInternational,
        cartItemIds: p.cart_item_ids ?? null,
      }
    })

  const smartInstallmentPaymentsWithoutFirstRefuse =
    formattedPayments[0]?.status === 'refused' && isSmartInstallment

  if (smartInstallmentPaymentsWithoutFirstRefuse) {
    formattedPayments.shift()
  }

  return formattedPayments
}

type FormattedPayment = ReturnType<typeof formatPayments>[0]

function isInternationalPayment(payment: IPayment) {
  return payment.international_quotation !== null
}

// HET-3828: pedido não pago exibia "R$ 0,00" porque o total somava só as linhas
// `paid`. Quem chega aqui pelo lembrete de pix/boleto (HET-3721) precisa
// justamente saber QUANTO pagar.
//
// O conserto é deliberadamente estreito: mantém a soma de pagos quando existe
// algum pago, e só cai no valor da cobrança quando não há nada pago. É o que
// evita mexer nas outras famílias de pedido, cada uma com um significado
// diferente para a mesma linha:
//
//   - cobrança única: as linhas são TENTATIVAS da mesma cobrança (medido em
//     produção, carrinho 68 tem 5 linhas de R$ 400 com `installments = 1`), e
//     somá-las multiplicaria o valor;
//   - multicard: as linhas são as partes de cada cartão e PRECISAM ser somadas
//     (carrinho 13072978, 2 × R$ 1.860 = R$ 3.720). O discriminador é
//     `carts.multi`, que o serializer não expõe, então aqui não dá para
//     distinguir multicard de retentativa — mais uma razão para não trocar a
//     regra de quem já tem pago;
//   - parcelamento (`installment`/`smart_installment`): a tela rotula este
//     número como "Total pago" (Order.tsx → t('amountPaid')), então somar só os
//     pagos é o comportamento CERTO e fica intocado.
export function getTotal(
  isSubscription: boolean,
  isInstallmentPlan: boolean,
  relevantPayment: FormattedPayment | undefined,
  payments: FormattedPayment[],
) {
  if (isSubscription) return relevantPayment?.amount

  const pagos = payments.filter((p) => p.status === PAYMENT_PAID_STATUS)

  // Havendo linha internacional, soma SÓ as internacionais: misturar centavos
  // em real com centavos em moeda estrangeira daria um número sem significado,
  // rotulado com a moeda de apenas uma das partes.
  const moeda = pagos.find((p) => p.isInternational)
  const somados = moeda ? pagos.filter((p) => p.isInternational) : pagos

  // `|| 0` porque `total_amount_to_be_paid_cents` é nullable no banco mesmo
  // sendo obrigatório no tipo: uma linha nula viraria NaN e o formatMoney
  // imprimiria "R$ NaN" na tela, sem lançar erro.
  const somaDosPagos = somados.reduce(
    (acc, p) => acc + (moeda ? p.currencyPriceCents : p.totalAmountCents || 0),
    0,
  )

  if (isInstallmentPlan || somaDosPagos > 0) {
    return formatMoney(
      somaDosPagos,
      moeda ? moneyOptionsFor(moeda.internationalQuotation) : undefined,
    )
  }

  // Nada pago ainda: mostra o valor da cobrança em aberto, que é o que a pessoa
  // veio descobrir. `amount` já sai na moeda certa quando é internacional.
  return relevantPayment?.amount
}

function formatOrderData(order: Order) {
  const mainItem = order.items.filter((i) => !i.order_bump_item_id)[0]
  const items = order.items.filter((i) => i.order_bump_item_id)

  const isSubscription = checkIsSubscription(order)

  const isStrategySales = checkIsStrategySales(order)

  const isSmartInstallment = checkIsSmartInstallment(order)

  const nextInvoiceAt = formatDateShort(order.subscription.next_invoice_at)

  const createdAt = formatDateShort(order.created_at)

  const payments = formatPayments(
    order.payments,
    isSmartInstallment,
    order.kind,
  )

  // Encontra o primeiro pagamento válido (que não é tentativa fracassada)
  const relevantPayment =
    payments.find(isChargeable) || (first(payments) as FormattedPayment)

  const isSmartInstallmentORStrategySales =
    isSmartInstallment || isStrategySales

  const total = getTotal(
    isSubscription,
    isSmartInstallmentORStrategySales,
    relevantPayment,
    payments,
  )
  const subscription =
    isSubscription || isStrategySales
      ? { ...order.subscription, nextInvoiceAt }
      : null

  const offerKind: OfferKind = order.offer_kind

  return {
    id: order.id,
    productTitle: mainItem.product.name,
    items,
    // HET-3540: todos os itens (principal + bumps) para o detalhe focado num item (?item=)
    allItems: order.items,
    total,
    isSubscription,
    isStrategySales,
    createdAt,
    payments,
    relevantPayment,
    subscription,
    raw: order,
    isSmartInstallmentORStrategySales,
    offerKind,
  }
}

export type FormattedOrderType = ReturnType<typeof formatOrderData>

export function useOrderQuery(orderId: string, isPublic?: boolean) {
  return useQuery(['orders', orderId], async () => {
    const fetchOrder = isPublic ? getPublicOrder : getOrder
    const { data } = await fetchOrder(orderId)

    return formatOrderData(data)
  })
}
